import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Clock, CheckCircle2, Building2, UploadCloud } from "lucide-react";
import PortalNav from "../components/PortalNav";
import { StatusBadge, EmptyState, isOverdue, OverdueBadge, STATUS } from "../components/shared";
import api from "../lib/api";

const ClientPortal = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);

  const load = async () => {
    try { const r = await api.get("/requests"); setRequests(r.data.requests); }
    catch (e) { /* handled */ }
  };
  useEffect(() => { load(); }, []);

  const toUpload = requests.filter((r) => [STATUS.PENDING, STATUS.RESUBMIT].includes(r.status)).length;
  const closed = requests.filter((r) => r.status === STATUS.CLOSED).length;

  return (
    <div className="min-h-screen bg-slate-50/50">
      <PortalNav title="Client Workspace" />
      <main className="mx-auto max-w-7xl px-6 py-10">
        <h1 className="text-2xl font-bold text-[#1a1a2e]">Your document requests</h1>
        <p className="mt-1 text-slate-500">Upload the requested documents and respond to your firm, all in one secure space.</p>

        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50"><FileText className="h-5 w-5 text-emerald-700" /></div>
            <div className="text-2xl font-bold text-[#1a1a2e]">{requests.length}</div><div className="text-sm text-slate-500">Total requests</div>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50"><Clock className="h-5 w-5 text-amber-700" /></div>
            <div className="text-2xl font-bold text-[#1a1a2e]">{toUpload}</div><div className="text-sm text-slate-500">Awaiting your upload</div>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50"><CheckCircle2 className="h-5 w-5 text-emerald-700" /></div>
            <div className="text-2xl font-bold text-[#1a1a2e]">{closed}</div><div className="text-sm text-slate-500">Closed</div>
          </div>
        </div>

        <div className="mt-10">
          {requests.length === 0 ? (
            <EmptyState title="No requests yet" subtitle="When your firm creates a document request for you, it will appear here for you to upload against." />
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {requests.map((r) => {
                const totalFiles = (r.tasks || []).reduce((s, t) => s + (t.files?.length || 0), 0);
                return (
                  <button key={r.id} onClick={() => navigate(`/request/${r.id}`)} className="rounded-2xl border border-slate-100 bg-white p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-xs text-slate-400"><Building2 className="h-3.5 w-3.5" /> {r.client_name}</div>
                        <h3 className="mt-1 text-lg font-bold text-[#1a1a2e]">{r.seq != null && <span data-testid={`req-seq-${r.id}`} className="mr-1.5 font-mono text-sm font-semibold text-slate-400">#{r.seq}</span>}{r.title}</h3>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <StatusBadge status={r.status} />
                        {isOverdue(r) && <OverdueBadge />}
                      </div>
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm text-slate-500">{r.description || "No description"}</p>
                    {r.status === STATUS.RESUBMIT && r.feedback && <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">Returned: {r.feedback}</p>}
                    <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1"><UploadCloud className="h-3.5 w-3.5" /> {totalFiles} files · {r.tasks?.length || 0} tasks</span>
                      <span>{r.deadline ? `Due ${r.deadline}` : ""}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ClientPortal;
